import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatPaginator, PageEvent } from '@angular/material';
import { VehicleModelsService, VehicleModelItem } from './vehicle-models.service';
import { Router } from '@angular/router';
import { OnboardingPageService } from '../onboarding-page/onboarding-page.service';
import { DataService } from '../data.service';
import { ConstantsService } from '../constants.service';
import { ToastMessage } from '../toast-message/toast-message.service';

@Component({
  selector: 'app-vehicle-models',
  template: require('./vehicle-models.component.html')
})
export class VehicleModelsComponent implements OnInit {
  modeId: string = '';
  filterType: string = '';
  filterActive: string = '';
  items: VehicleModelItem[] = [];
  displayedColumns: string[] = ['model', 'manufacturer', 'displayName', 'edit'];
  isLoading = false;
  modes: Array<{ _id: string, mode: string }> = [];

  // pagination
  total = 0;
  pageSize = 10;
  pageIndex = 0;

  @ViewChild(MatPaginator) paginator: MatPaginator;

  constructor(
    private route: ActivatedRoute,
    private service: VehicleModelsService,
    private router: Router,
    private onboardingService: OnboardingPageService,
    private dataService: DataService,
    private constantService: ConstantsService,
    private toast: ToastMessage
  ) {}

  ngOnInit() {
    // No UI gating; actions will authorize on click

    this.modeId = this.route.snapshot.queryParamMap.get('modeId') || '';
    const qpBodyType = this.route.snapshot.queryParamMap.get('bodyType');
    const qpActive = this.route.snapshot.queryParamMap.get('active');
    const qpPageIndex = this.route.snapshot.queryParamMap.get('pageIndex');
    const qpPageSize = this.route.snapshot.queryParamMap.get('pageSize');
    const qpOffset = this.route.snapshot.queryParamMap.get('offset');
    this.filterType = qpBodyType || '';
    this.filterActive = qpActive || '';
    if (qpPageSize && !isNaN(+qpPageSize)) {
      this.pageSize = parseInt(qpPageSize as any, 10);
    }
    if (qpPageIndex && !isNaN(+qpPageIndex)) {
      this.pageIndex = parseInt(qpPageIndex as any, 10);
    } else if (qpOffset && !isNaN(+qpOffset)) {
      const off = parseInt(qpOffset as any, 10);
      this.pageIndex = this.pageSize > 0 ? Math.floor(off / this.pageSize) : 0;
    }
    this.loadModes();
    this.fetchCurrentPage();
  }

  onPage(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.fetchCurrentPage();
  }

  private loadModes() {
    // Try localStorage first, fallback to cookie scan
    let userId = localStorage.getItem('userId') || '';
    if (!userId) {
      const cookies = document.cookie.split(';');
      const idCookie = cookies.find(c => c.includes('_id'));
      if (idCookie) {
        const parts = idCookie.split('=');
        if (parts.length > 1) {
          try {
            // some places store JSON, others plain
            const raw = parts[1].trim();
            userId = JSON.parse(raw)['_id'] || raw;
          } catch (_) {
            userId = parts[1].trim();
          }
        }
      }
    }
    if (!userId) { return; }
    this.onboardingService.getModes(userId).subscribe(
      (resp: any) => {
        const data = resp && resp['data'] && resp['data']['modes'] && resp['data']['modes']['data'];
        if (Array.isArray(data)) {
          this.modes = data.map((m: any) => ({ _id: m._id, mode: m.mode || m.name || '' }));
        }
      },
      () => {}
    );
  }

  applyFilters() {
    this.pageIndex = 0;
    this.fetchCurrentPage();
  }

  clearFilters() {
    this.filterType = '';
    this.filterActive = '';
    this.modeId = '';
    this.pageIndex = 0;
    this.fetchCurrentPage();
  }

  private fetchCurrentPage() {
    this.isLoading = true;
    const offset = this.pageIndex * this.pageSize;
    const activeParam = this.filterActive === '' ? undefined : (this.filterActive === 'true' ? true : false);
    this.service.fetchVehicleModels(this.modeId, this.pageSize, offset, this.filterType, activeParam).subscribe(
      (res) => {
        this.items = res.items || [];
        this.total = Number.isFinite(res.total) ? res.total : 0;
        this.isLoading = false;
      },
      () => {
        this.items = [];
        this.total = 0;
        this.isLoading = false;
      }
    );
  }

  onEdit(row: VehicleModelItem) {
    // Auth check for edit
    const editRole = this.constantService.vehicleModelPermissions.edit;
    if (!this.userHasRole(editRole)) {
      this.toast.openSnackBarError('You are not authorized to edit vehicle models');
      return;
    }
    this.router.navigate(
      ['/vehicle-models', row._id as string, 'edit'],
      {
        queryParams: {
          model: row.model,
          manufacturer: row.manufacturer,
          displayName: row.displayName,
          modeId: this.modeId || '',
          bodyType: this.filterType || '',
          active: (row as any).active !== undefined ? (row as any).active : this.filterActive || '',
          pageIndex: this.pageIndex,
          pageSize: this.pageSize,
          offset: this.pageIndex * this.pageSize
        },
        queryParamsHandling: 'merge'
      }
    );
  }

  onAdd() {
    // Auth check for add
    const addRole = this.constantService.vehicleModelPermissions.add;
    if (!this.userHasRole(addRole)) {
      this.toast.openSnackBarError('You are not authorized to add vehicle models');
      return;
    }
    this.router.navigate(
      ['/vehicle-models', 'create'],
      {
        queryParams: {
          modeId: this.modeId || '',
          bodyType: this.filterType || '',
          active: this.filterActive || '',
          pageIndex: this.pageIndex,
          pageSize: this.pageSize,
          offset: this.pageIndex * this.pageSize
        },
        queryParamsHandling: 'merge'
      }
    );
  }

  private userHasRole(role: string): boolean {
    const accessRoles = this.dataService.accessCookie('accessRoles') || '';
    const roles = accessRoles ? accessRoles.split(',') : [];
    return Array.isArray(roles) && roles.indexOf(role) > -1;
  }

  onBack() {
    this.router.navigate(['/modelSelection'], { queryParamsHandling: 'preserve' });
  }
}
